/**
 * SecondScreen - Multi-panel view for second monitor
 *
 * Displays AI Chat, Analysis, Overview, and Actions panels in a grid layout
 * with ability to float/drag panels for flexible workspace arrangement.
 */

import { useState, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { Sun, Moon, Bot, Sparkles, RefreshCw, Send, Loader2, Trash2, Lightbulb, BookOpen, Copy, Search, Play, CheckCircle, Square, ArrowUpCircle, PenLine, Crown, Users, Wand2, Wrench, Clock, ChevronDown, FileText, AlertTriangle, X, UserPlus, Mic } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useDarkMode } from '@/hooks/useDarkMode'
import { useLiteContext } from '@/hooks/useLiteContext'
import { useAnalysis } from '@/hooks/useAnalysis'
import { useChat } from '@/hooks/useChat'
import { useActions } from '@/hooks/useActions'
import { useResponseGenerator } from '@/hooks/useResponseGenerator'
import { performAction, logTimeEntry } from '@/services/api'
import { useCallRecordingContext } from '@/contexts/CallRecordingContext'
import { CallRecorderModal } from '@/components/calls/CallRecorderModal'
import { FloatingRecorderWidget } from '@/components/calls/FloatingRecorderWidget'
import { CallNotesModal } from '@/components/calls/CallNotesModal'
import type { MetricScore, SkillsMetricScore, MultiUserEventMetricScore, ResponseTone, ResponseLength } from '@/services/api'

// Metric configuration
type MetricKey = 'clarity' | 'sentiment' | 'empathy' | 'acumen' | 'cadence' | 'disruption' | 'age' | 'priority' | 'chronic' | 'skills' | 'multiUserEvent' | 'responseQuality'

interface MetricConfig {
  key: MetricKey
  label: string
  invertColor?: boolean
}

const METRICS: MetricConfig[] = [
  { key: 'clarity', label: 'CLARITY' },
  { key: 'sentiment', label: 'SENTIMENT' },
  { key: 'empathy', label: 'EMPATHY' },
  { key: 'acumen', label: 'ACUMEN', invertColor: true },
  { key: 'cadence', label: 'CADENCE' },
  { key: 'disruption', label: 'DISRUPTION', invertColor: true },
  { key: 'age', label: 'AGE', invertColor: true },
  { key: 'priority', label: 'PRIORITY', invertColor: true },
  { key: 'chronic', label: 'CHRONIC', invertColor: true },
  { key: 'skills', label: 'SKILLS', invertColor: true },
  { key: 'multiUserEvent', label: 'MULTI-USER' },
  { key: 'responseQuality', label: 'RESPONSE' },
]

function getScoreColor(score: number, invert: boolean = false, isDark: boolean = false): string {
  const effectiveScore = invert ? 11 - score : score
  if (isDark) {
    if (effectiveScore >= 7) return 'bg-emerald-900/50 border-emerald-600'
    if (effectiveScore >= 4) return 'bg-amber-900/50 border-amber-600'
    return 'bg-red-900/50 border-red-600'
  }
  if (effectiveScore >= 7) return 'bg-emerald-50 border-emerald-300'
  if (effectiveScore >= 4) return 'bg-amber-50 border-amber-300'
  return 'bg-red-50 border-red-300'
}

function getScoreTextColor(score: number, invert: boolean = false): string {
  const effectiveScore = invert ? 11 - score : score
  if (effectiveScore >= 7) return 'text-emerald-500'
  if (effectiveScore >= 4) return 'text-amber-500'
  return 'text-red-500'
}

export function SecondScreen() {
  // Sync dark mode from URL param (for popups opened from cross-origin iframes)
  const darkUrlParam = new URLSearchParams(window.location.search).get('dark')
  if (darkUrlParam !== null) {
    localStorage.setItem('snpod-dark-mode', darkUrlParam === '1' ? 'true' : 'false')
  }
  const { isDark, toggle: toggleDarkMode } = useDarkMode()
  const { context, loading, error, refetch } = useLiteContext()
  const { analysis, loading: analysisLoading, analyze } = useAnalysis()
  const { messages, loading: chatLoading, error: chatError, sendMessage, clearChat } = useChat(context?.sys_id)
  const { noteLoading, noteSuccess, submitNote, resetNoteStatus, commentLoading, commentSuccess, submitComment, resetCommentStatus } = useActions()
  const { generatedResponse, loading: responseLoading, generate: generateCustomerResponse, clear: clearGeneratedResponse } = useResponseGenerator()

  const [chatInput, setChatInput] = useState('')
  const [noteInput, setNoteInput] = useState('')
  const [noteType, setNoteType] = useState<'work_note' | 'comment'>('work_note')
  const [responseTone, setResponseTone] = useState<ResponseTone>('professional')
  const responseLength: ResponseLength = 'concise'
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [copySuccess, setCopySuccess] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)

  // Time entry state
  const [timeMinutes, setTimeMinutes] = useState(15) // Default to 15 min
  const [timeLoading, setTimeLoading] = useState(false)
  const [timeSuccess, setTimeSuccess] = useState(false)

  // Status change dropdown
  const [showStatusMenu, setShowStatusMenu] = useState(false)

  // Selected metric for tooltip
  const [selectedMetric, setSelectedMetric] = useState<MetricKey | null>(null)

  // Recording state
  const { status: recordingStatus, isRecording } = useCallRecordingContext()
  const [showRecorderModal, setShowRecorderModal] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)

  // Screen pop alerts - track which ones are dismissed
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set())

  // Reset dismissed alerts when context changes (new ticket)
  useEffect(() => {
    if (context?.sys_id) {
      setDismissedAlerts(new Set())
    }
  }, [context?.sys_id])

  // Rotating loading messages
  const LOADING_MESSAGES = [
    "Analyzing your request...",
    "Searching the knowledge base...",
    "Hang on a moment more...",
    "This will be worth the wait...",
    "Parsing data, just a moment...",
    "Consulting the AI brain...",
    "Digging through the archives...",
    "Almost there...",
    "Crunching the numbers...",
    "Finding the best answer...",
  ]
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)

  // Rotate loading messages every 2.5 seconds
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null
    if (chatLoading || analysisLoading) {
      interval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length)
      }, 2500)
    } else {
      setLoadingMessageIndex(0)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [chatLoading, analysisLoading])

  // Editable response text (synced from generated response)
  const [editableResponse, setEditableResponse] = useState('')

  // Sync generated response to editable state
  useEffect(() => {
    if (generatedResponse) {
      setEditableResponse(generatedResponse)
    }
  }, [generatedResponse])

  // Auto-analyze on load
  useEffect(() => {
    if (context && !analysis && !analysisLoading) {
      analyze(context)
    }
  }, [context, analysis, analysisLoading, analyze])

  // Chat scroll
  useEffect(() => {
    if (messages.length > 0) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [messages])

  // Send chat message
  const handleSendMessage = () => {
    if (!chatInput.trim() || !context) return
    sendMessage(context, chatInput)
    setChatInput('')
  }

  // Quick actions
  const handleQuickAction = async (action: 'resolve' | 'close' | 'reopen') => {
    if (!context) return
    setActionLoading(action)
    try {
      await performAction({
        action,
        sys_id: context.sys_id,
        table: context.table,
        close_notes: action === 'resolve' || action === 'close' ? 'Resolved via SNPod AI Assistant' : undefined,
        close_code: action === 'resolve' || action === 'close' ? 'Solution provided' : undefined,
      })
      refetch()
    } catch (err: any) {
      console.error(`[Action] ${action} failed:`, err.message)
    } finally {
      setActionLoading(null)
    }
  }

  // Submit note
  const handleSubmitNote = async () => {
    if (!context || !noteInput.trim()) return
    const success = noteType === 'work_note'
      ? await submitNote(context.sys_id, context.table, noteInput)
      : await submitComment(context.sys_id, context.table, noteInput)
    if (success) {
      setNoteInput('')
      setTimeout(() => { resetNoteStatus(); resetCommentStatus(); }, 3000)
    }
  }

  // Generate response
  const handleGenerateResponse = () => {
    if (context) generateCustomerResponse(context, responseTone, responseLength)
  }

  // Submit time entry
  const handleSubmitTimeEntry = async () => {
    if (!context || timeMinutes === 0) return
    setTimeLoading(true)
    try {
      await logTimeEntry({
        sys_id: context.sys_id,
        table: context.table,
        hours: Math.floor(timeMinutes / 60),
        minutes: timeMinutes % 60,
        billable: true,
      })
      setTimeSuccess(true)
      setTimeMinutes(0)
      setTimeout(() => setTimeSuccess(false), 3000)
    } catch (err) {
      console.error('[SecondScreen] Time entry failed:', err)
    } finally {
      setTimeLoading(false)
    }
  }

  // Change status
  const handleStatusChange = async (newState: string) => {
    if (!context) return
    setShowStatusMenu(false)
    setActionLoading('status')
    try {
      await performAction({
        action: 'update_state',
        sys_id: context.sys_id,
        table: context.table,
        state: newState,
      })
      refetch()
    } catch (err: any) {
      console.error('[SecondScreen] Status change failed:', err.message)
    } finally {
      setActionLoading(null)
    }
  }

  // Copy response
  const handleCopyResponse = async () => {
    if (editableResponse) {
      await navigator.clipboard.writeText(editableResponse)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    }
  }

  // Clear editable response when dismissing
  const handleDismissResponse = () => {
    setEditableResponse('')
    clearGeneratedResponse()
  }

  return (
    <div className={`h-screen w-screen overflow-hidden p-4 transition-colors ${isDark ? 'bg-slate-900' : 'bg-gray-100'}`}>
      {/* Header - Compact */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <span className="text-rezolve-green">SNPOD</span>
            <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>|</span>
            <span className={isDark ? 'text-white' : 'text-gray-900'}>{context?.number || 'Loading...'}</span>
          </h1>
          <span className={`text-sm truncate max-w-md ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {context?.short_description || ''}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => refetch()} className="h-8 w-8" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${isDark ? 'text-slate-300' : 'text-gray-600'}`} />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="h-8 w-8" title={isDark ? "Light Mode" : "Dark Mode"}>
            {isDark ? <Sun className="h-4 w-4 text-slate-300" /> : <Moon className="h-4 w-4 text-gray-600" />}
          </Button>
        </div>
      </div>

      {/* Screen Pop Alerts - VIP, Escalation, Multi-User */}
      {analysis && (
        <div className="mb-3 space-y-2">
          {/* VIP Alert */}
          {analysis.sentiment?.metrics?.vip?.isVip && !dismissedAlerts.has('vip') && (
            <div className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 animate-pulse ${isDark ? 'bg-amber-900/50 border-amber-500' : 'bg-amber-100 border-amber-500'}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${isDark ? 'bg-amber-500' : 'bg-amber-500'}`}>
                  <Crown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className={`font-bold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>VIP / Executive Contact</div>
                  <div className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>{analysis.sentiment.metrics.vip.reason || 'High-priority contact detected'}</div>
                </div>
              </div>
              <button onClick={() => setDismissedAlerts(prev => new Set([...prev, 'vip']))} className={`p-1 rounded hover:bg-amber-500/20 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Escalation Alert */}
          {analysis.sentiment?.escalation?.recommended && !dismissedAlerts.has('escalation') && (
            <div className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 animate-pulse ${isDark ? 'bg-red-900/50 border-red-500' : 'bg-red-100 border-red-500'}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${isDark ? 'bg-red-500' : 'bg-red-500'}`}>
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className={`font-bold ${isDark ? 'text-red-300' : 'text-red-800'}`}>Escalation Recommended</div>
                  <div className={`text-sm ${isDark ? 'text-red-400' : 'text-red-700'}`}>{analysis.sentiment.escalation.factors?.[0] || 'This ticket may require escalation to a higher tier'}</div>
                </div>
              </div>
              <button onClick={() => setDismissedAlerts(prev => new Set([...prev, 'escalation']))} className={`p-1 rounded hover:bg-red-500/20 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Multi-User Event Alert */}
          {(analysis.sentiment?.metrics?.multiUserEvent as MultiUserEventMetricScore)?.isMultiUser && !dismissedAlerts.has('multiuser') && (
            <div className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 animate-pulse ${isDark ? 'bg-violet-900/50 border-violet-500' : 'bg-violet-100 border-violet-500'}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${isDark ? 'bg-violet-500' : 'bg-violet-500'}`}>
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className={`font-bold ${isDark ? 'text-violet-300' : 'text-violet-800'}`}>Multi-User Event Detected</div>
                  <div className={`text-sm ${isDark ? 'text-violet-400' : 'text-violet-700'}`}>{(analysis.sentiment.metrics.multiUserEvent as MultiUserEventMetricScore).affectedScope || 'Multiple users may be affected by this issue'}</div>
                </div>
              </div>
              <button onClick={() => setDismissedAlerts(prev => new Set([...prev, 'multiuser']))} className={`p-1 rounded hover:bg-violet-500/20 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Loading/Error states */}
      {loading && (
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <Loader2 className="h-8 w-8 animate-spin text-rezolve-green" />
          <span className={`ml-2 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Loading ticket data...</span>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <div className="text-center">
            <p className="text-red-500 mb-2">{error}</p>
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </div>
      )}

      {/* New Layout: Ask Bot + Analysis on top (large), Actions bar at bottom (compact) */}
      {!loading && !error && (
        <div className="flex flex-col h-[calc(100vh-80px)] gap-3">
          {/* Top Row: Ask Bot + Analysis side by side */}
          <div className="flex-1 grid grid-cols-2 gap-3 min-h-0">
            {/* Ask Bot Panel - Left */}
            <div className={`relative rounded-lg shadow-lg border overflow-hidden flex flex-col ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
              <div className={`flex items-center gap-2 px-4 py-2 border-b ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                <Bot className="h-5 w-5 text-rezolve-green" />
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Ask Bot</span>
                <Button variant="ghost" size="icon" onClick={clearChat} disabled={messages.length === 0} className="ml-auto h-7 w-7">
                  <Trash2 className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <Bot className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-rezolve-green/70' : 'text-rezolve-green/50'}`} />
                    <p className={`text-base mb-6 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Ask me anything about this ticket!</p>
                    <div className="grid grid-cols-3 gap-2 max-w-xl mx-auto">
                      <button onClick={() => setChatInput('What is the quickest way to resolve this issue?')}
                        className={`text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 ${isDark ? 'bg-slate-700 border-slate-600 hover:border-rezolve-green/50 hover:bg-slate-600 text-slate-200' : 'bg-white border-gray-200 hover:border-rezolve-green/50 hover:bg-rezolve-green/5 text-gray-700'}`}>
                        <Lightbulb className="h-5 w-5 text-rezolve-green flex-shrink-0" />
                        <span>Quick resolution?</span>
                      </button>
                      <button onClick={() => setChatInput('Find documentation or knowledge base articles for this issue')}
                        className={`text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 ${isDark ? 'bg-slate-700 border-slate-600 hover:border-blue-500/50 hover:bg-slate-600 text-slate-200' : 'bg-white border-gray-200 hover:border-blue-500/50 hover:bg-blue-50 text-gray-700'}`}>
                        <BookOpen className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        <span>Find KB articles</span>
                      </button>
                      <button onClick={() => setChatInput('Find similar tickets and how they were resolved')}
                        className={`text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 ${isDark ? 'bg-slate-700 border-slate-600 hover:border-purple-500/50 hover:bg-slate-600 text-slate-200' : 'bg-white border-gray-200 hover:border-purple-500/50 hover:bg-purple-50 text-gray-700'}`}>
                        <Copy className="h-5 w-5 text-purple-500 flex-shrink-0" />
                        <span>Similar tickets</span>
                      </button>
                      <button onClick={() => setChatInput('What are the possible root causes of this issue?')}
                        className={`text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 ${isDark ? 'bg-slate-700 border-slate-600 hover:border-amber-500/50 hover:bg-slate-600 text-slate-200' : 'bg-white border-gray-200 hover:border-amber-500/50 hover:bg-amber-50 text-gray-700'}`}>
                        <Search className="h-5 w-5 text-amber-500 flex-shrink-0" />
                        <span>Root causes?</span>
                      </button>
                      <button onClick={() => setChatInput('Are there any runbooks for this type of issue?')}
                        className={`text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 ${isDark ? 'bg-slate-700 border-slate-600 hover:border-cyan-500/50 hover:bg-slate-600 text-slate-200' : 'bg-white border-gray-200 hover:border-cyan-500/50 hover:bg-cyan-50 text-gray-700'}`}>
                        <FileText className="h-5 w-5 text-cyan-500 flex-shrink-0" />
                        <span>Find runbooks</span>
                      </button>
                      <button onClick={() => setChatInput('Find resources or individuals who can help escalate this ticket')}
                        className={`text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 ${isDark ? 'bg-slate-700 border-slate-600 hover:border-orange-500/50 hover:bg-slate-600 text-slate-200' : 'bg-white border-gray-200 hover:border-orange-500/50 hover:bg-orange-50 text-gray-700'}`}>
                        <UserPlus className="h-5 w-5 text-orange-500 flex-shrink-0" />
                        <span>Escalation</span>
                      </button>
                    </div>
                  </div>
                )}
                {messages.filter(msg => msg.content?.trim()).map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] p-4 rounded-lg ${msg.role === 'user' ? 'bg-rezolve-green text-white' : isDark ? 'bg-slate-700 border border-slate-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-1 mb-2 text-rezolve-green font-medium text-xs">
                          <Bot className="h-3 w-3" /> AI Assistant
                        </div>
                      )}
                      {msg.role === 'assistant' ? (
                        <div className="text-sm leading-normal">
                          <ReactMarkdown
                            components={{
                              a: ({ href, children }) => (
                                <a href={href} target="_blank" rel="noopener noreferrer" className="text-rezolve-green font-semibold hover:underline">
                                  {children}
                                </a>
                              ),
                              h1: ({ children }) => <h1 className="text-base font-bold mt-3 mb-2 first:mt-0">{children}</h1>,
                              h2: ({ children }) => <h2 className="text-base font-bold mt-3 mb-2 first:mt-0">{children}</h2>,
                              h3: ({ children }) => <h3 className="text-sm font-bold mt-3 mb-1.5 first:mt-0">{children}</h3>,
                              h4: ({ children }) => <h4 className="text-sm font-semibold mt-2 mb-1 first:mt-0">{children}</h4>,
                              p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
                              ul: ({ children }) => <ul className="my-2 ml-4 space-y-1">{children}</ul>,
                              ol: ({ children }) => <ol className="my-2 ml-4 space-y-1 list-decimal">{children}</ol>,
                              li: ({ children }) => (
                                <li className="flex items-start gap-2">
                                  <span className="text-rezolve-green mt-1.5 text-[8px]">●</span>
                                  <span className="flex-1">{children}</span>
                                </li>
                              ),
                              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                              em: ({ children }) => <em className="italic">{children}</em>,
                              code: ({ children }) => (
                                <code className={`px-1.5 py-0.5 rounded text-xs font-mono ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`}>
                                  {children}
                                </code>
                              ),
                              hr: () => <hr className={`my-3 border-t ${isDark ? 'border-slate-600' : 'border-gray-300'}`} />,
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                      )}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700 border border-slate-600' : 'bg-gray-100'}`}>
                      <div className="flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin text-rezolve-green" />
                        <span className={`text-sm animate-pulse ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                          {LOADING_MESSAGES[loadingMessageIndex]}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {chatError && (
                  <div className="flex justify-start">
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200'}`}>
                      <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-600'}`}>{chatError}</p>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              {/* Input */}
              <div className={`p-3 border-t ${isDark ? 'border-slate-600' : 'border-gray-200'}`}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    placeholder="Ask a question about this ticket..."
                    className={`flex-1 px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rezolve-green/50 ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                    disabled={chatLoading}
                  />
                  <Button onClick={handleSendMessage} disabled={chatLoading || !chatInput.trim()} className="bg-rezolve-green hover:bg-rezolve-green/90 px-4">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Analysis Panel - Right */}
            <div className={`relative rounded-lg shadow-lg border overflow-hidden flex flex-col ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
              <div className={`flex items-center gap-2 px-4 py-2 border-b ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                <Sparkles className="h-5 w-5 text-rezolve-green" />
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>AI Analysis</span>
                {!analysis && !analysisLoading && (
                  <Button onClick={() => context && analyze(context)} size="sm" className="ml-auto bg-rezolve-green hover:bg-rezolve-green/90 h-7 text-xs">
                    <Sparkles className="h-3 w-3 mr-1" /> Analyze
                  </Button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {analysisLoading && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Loader2 className="h-10 w-10 animate-spin mx-auto text-rezolve-green" />
                      <p className={`mt-3 text-sm animate-pulse ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                        {LOADING_MESSAGES[loadingMessageIndex]}
                      </p>
                    </div>
                  </div>
                )}
                {!analysis && !analysisLoading && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Sparkles className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-rezolve-green/50' : 'text-gray-300'}`} />
                      <p className={`${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Click Analyze to get AI insights</p>
                    </div>
                  </div>
                )}
                {analysis && (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-rezolve-green/30' : 'bg-gradient-to-r from-rezolve-green/10 to-rezolve-green/5 border-rezolve-green/20'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-4 w-4 text-rezolve-green" />
                        <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>AI Summary</h3>
                      </div>
                      <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{analysis.summary}</p>
                    </div>

                    {/* VIP + Multi-User badges row */}
                    <div className="flex gap-3">
                      {analysis.sentiment?.metrics?.vip?.isVip && (
                        <div className={`flex-1 p-3 rounded-lg border ${isDark ? 'bg-amber-900/30 border-amber-600' : 'bg-amber-50 border-amber-300'}`}>
                          <div className="flex items-center gap-2">
                            <Crown className="h-5 w-5 text-amber-500" />
                            <div>
                              <div className={`font-semibold text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>VIP Contact</div>
                              <div className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{analysis.sentiment.metrics.vip.reason}</div>
                            </div>
                          </div>
                        </div>
                      )}
                      {(analysis.sentiment?.metrics?.multiUserEvent as MultiUserEventMetricScore)?.isMultiUser && (
                        <div className={`flex-1 p-3 rounded-lg border ${isDark ? 'bg-violet-900/30 border-violet-600' : 'bg-violet-50 border-violet-300'}`}>
                          <div className="flex items-center gap-2">
                            <Users className="h-5 w-5 text-violet-500" />
                            <div>
                              <div className={`font-semibold text-sm ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>Multi-User Event</div>
                              <div className={`text-xs ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                                {(analysis.sentiment.metrics.multiUserEvent as MultiUserEventMetricScore).affectedScope || 'Multiple users affected'}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Metrics Grid - 4x2 with clickable tooltips */}
                    <div className="grid grid-cols-4 gap-2">
                      {METRICS.slice(0, 8).map((metric) => {
                        const metricData = analysis.sentiment?.metrics?.[metric.key] as MetricScore | undefined
                        const score = metricData?.score || 0
                        const explanation = metricData?.explanation || ''
                        if (metric.key === 'multiUserEvent') return null
                        return (
                          <button
                            key={metric.key}
                            onClick={() => setSelectedMetric(selectedMetric === metric.key ? null : metric.key)}
                            title={explanation || `${metric.label}: Score ${score}/10`}
                            className={`rounded-lg border p-2 text-center cursor-pointer transition-all hover:scale-105 ${getScoreColor(score, metric.invertColor, isDark)} ${selectedMetric === metric.key ? 'ring-2 ring-rezolve-green ring-offset-1' : ''}`}
                          >
                            <div className={`text-[9px] font-bold mb-1 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{metric.label}</div>
                            <div className={`text-xl font-bold ${getScoreTextColor(score, metric.invertColor)}`}>{score}</div>
                          </button>
                        )
                      })}
                    </div>

                    {/* Selected Metric Explanation */}
                    {selectedMetric && analysis.sentiment?.metrics?.[selectedMetric] && (
                      <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/80 border-rezolve-green/50' : 'bg-rezolve-green/5 border-rezolve-green/30'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                            {METRICS.find(m => m.key === selectedMetric)?.label} Explanation
                          </span>
                          <button onClick={() => setSelectedMetric(null)} className={`text-xs ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>✕</button>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                          {(analysis.sentiment.metrics[selectedMetric] as MetricScore).explanation}
                        </p>
                      </div>
                    )}

                    {/* Skills */}
                    {analysis.sentiment?.metrics?.skills?.required && (analysis.sentiment.metrics.skills as SkillsMetricScore).required.length > 0 && (
                      <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-blue-50 border-blue-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Wrench className="h-4 w-4 text-blue-500" />
                          <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-blue-800'}`}>Skills Required</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(analysis.sentiment.metrics.skills as SkillsMetricScore).required.slice(0, 6).map((skill, idx) => (
                            <span key={idx} className={`px-2.5 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-blue-900/50 border border-blue-600 text-blue-300' : 'bg-blue-100 border border-blue-300 text-blue-700'}`}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Escalation */}
                    {analysis.sentiment?.escalation && (
                      <div className={`p-3 rounded-lg border ${analysis.sentiment.escalation.recommended ? (isDark ? 'bg-red-900/30 border-red-600' : 'bg-red-50 border-red-300') : (isDark ? 'bg-emerald-900/30 border-emerald-600' : 'bg-emerald-50 border-emerald-300')}`}>
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Escalation</span>
                          <span className={`px-2.5 py-1 rounded text-xs font-medium ${analysis.sentiment.escalation.recommended ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`}>
                            {analysis.sentiment.escalation.recommended ? 'Recommended' : 'Not Needed'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Suggested Actions */}
                    {analysis.suggestedActions && analysis.suggestedActions.length > 0 && (
                      <div>
                        <h3 className={`font-medium text-sm mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                          <Lightbulb className="h-4 w-4 text-amber-500" /> Suggested Actions
                        </h3>
                        <ul className="space-y-2">
                          {analysis.suggestedActions.map((action, i) => (
                            <li key={i} className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                              <span className="text-rezolve-green font-bold">{i + 1}.</span>
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Generated Response Preview - Above actions bar when available */}
          {editableResponse && (
            <div className={`flex-none rounded-lg shadow-lg border p-3 ${isDark ? 'bg-slate-800 border-rezolve-green/50' : 'bg-white border-rezolve-green/30'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-bold flex items-center gap-1.5 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  <Sparkles className="h-3.5 w-3.5 text-rezolve-green" /> Generated Response
                  <span className={`text-[10px] font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>(editable)</span>
                </span>
                <div className="flex gap-1">
                  <Button onClick={handleCopyResponse} variant="outline" size="sm" className="h-6 text-xs px-2">
                    {copySuccess ? <CheckCircle className="h-3 w-3 text-green-500" /> : <><Copy className="h-3 w-3 mr-1" />Copy</>}
                  </Button>
                  <Button onClick={() => { setNoteInput(editableResponse); handleDismissResponse(); }} variant="outline" size="sm" className="h-6 text-xs px-2">
                    <ArrowUpCircle className="h-3 w-3 mr-1" />Use
                  </Button>
                  <Button onClick={handleDismissResponse} variant="ghost" size="sm" className="h-6 text-xs px-1.5 text-red-500">✕</Button>
                </div>
              </div>
              <textarea
                value={editableResponse}
                onChange={(e) => setEditableResponse(e.target.value)}
                rows={3}
                className={`w-full text-sm rounded border p-2 resize-none focus:outline-none focus:ring-2 focus:ring-rezolve-green/50 ${isDark ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400' : 'bg-gray-50 border-gray-200 text-gray-700'}`}
                placeholder="Edit your response here..."
              />
            </div>
          )}

          {/* Bottom Row: Actions Bar - Compact horizontal layout */}
          <div className={`flex-none rounded-lg shadow-lg border ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3 p-3">
              {/* Quick Actions + Change Status */}
              <div className="flex items-center gap-2 relative">
                <Play className="h-4 w-4 text-emerald-500" />
                {context && context.state !== '6' && context.state !== '7' ? (
                  <>
                    <Button onClick={() => handleQuickAction('resolve')} disabled={actionLoading !== null} size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs">
                      {actionLoading === 'resolve' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle className="h-3 w-3 mr-1" />Resolve</>}
                    </Button>
                    <Button onClick={() => handleQuickAction('close')} disabled={actionLoading !== null} size="sm" className="bg-slate-600 hover:bg-slate-700 h-8 text-xs">
                      {actionLoading === 'close' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Square className="h-3 w-3 mr-1" />Close</>}
                    </Button>
                    {/* Change Status dropdown */}
                    <div className="relative">
                      <Button onClick={() => setShowStatusMenu(!showStatusMenu)} disabled={actionLoading !== null} variant="outline" size="sm" className="h-8 text-xs">
                        {actionLoading === 'status' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ChevronDown className="h-3 w-3 mr-1" />Status</>}
                      </Button>
                      {showStatusMenu && (
                        <div className={`absolute bottom-full left-0 mb-1 w-36 rounded-lg shadow-lg border z-50 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-200'}`}>
                          <button onClick={() => handleStatusChange('1')} className={`w-full text-left px-3 py-2 text-xs hover:bg-rezolve-green/10 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>New</button>
                          <button onClick={() => handleStatusChange('2')} className={`w-full text-left px-3 py-2 text-xs hover:bg-rezolve-green/10 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>In Progress</button>
                          <button onClick={() => handleStatusChange('3')} className={`w-full text-left px-3 py-2 text-xs hover:bg-rezolve-green/10 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>On Hold</button>
                          <button onClick={() => handleStatusChange('-5')} className={`w-full text-left px-3 py-2 text-xs hover:bg-rezolve-green/10 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>Pending</button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <Button onClick={() => handleQuickAction('reopen')} disabled={actionLoading !== null} size="sm" className="bg-amber-600 hover:bg-amber-700 h-8 text-xs">
                    {actionLoading === 'reopen' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" />Reopen</>}
                  </Button>
                )}
              </div>

              {/* Divider */}
              <div className={`w-px h-8 ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`} />

              {/* Time Entry - Compact with editable input */}
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-purple-500 flex-shrink-0" />
                <div className="flex gap-0.5">
                  {[15, 30, 45, 60].map((mins) => (
                    <button
                      key={mins}
                      onClick={() => setTimeMinutes(mins)}
                      className={`text-[10px] px-1.5 py-1 rounded transition-colors ${timeMinutes === mins ? 'bg-purple-600 text-white' : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {mins === 60 ? '1h' : `${mins}m`}
                    </button>
                  ))}
                </div>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="480"
                    value={timeMinutes || ''}
                    onChange={(e) => setTimeMinutes(Math.min(480, Math.max(0, parseInt(e.target.value) || 0)))}
                    placeholder="min"
                    className={`w-12 text-center text-[11px] px-1 py-1 rounded-l border-r-0 focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-slate-700 border border-slate-600 text-white placeholder-slate-500' : 'bg-white border border-gray-300 text-gray-800 placeholder-gray-400'}`}
                  />
                  <span className={`text-[10px] px-1.5 py-1 rounded-r border border-l-0 ${isDark ? 'bg-slate-600 border-slate-600 text-slate-400' : 'bg-gray-100 border-gray-300 text-gray-500'}`}>m</span>
                </div>
                <Button onClick={handleSubmitTimeEntry} disabled={timeLoading || timeMinutes === 0} size="sm" className="bg-purple-600 hover:bg-purple-700 h-8 text-xs px-2">
                  {timeLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Log'}
                </Button>
                {timeSuccess && <CheckCircle className="h-4 w-4 text-purple-500" />}
              </div>

              {/* Divider */}
              <div className={`w-px h-8 ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`} />

              {/* Add Note - Inline */}
              <div className="flex items-center gap-2 flex-1">
                <PenLine className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <div className="flex gap-0.5">
                  <button onClick={() => setNoteType('work_note')} className={`text-[10px] px-1.5 py-1 rounded-l ${noteType === 'work_note' ? 'bg-rezolve-green text-white' : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>Int</button>
                  <button onClick={() => setNoteType('comment')} className={`text-[10px] px-1.5 py-1 rounded-r ${noteType === 'comment' ? 'bg-blue-500 text-white' : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>Cust</button>
                </div>
                <input
                  type="text"
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmitNote()}
                  placeholder={noteType === 'work_note' ? 'Internal note...' : 'Customer comment...'}
                  className={`flex-1 min-w-[120px] px-2 py-1.5 border rounded text-xs ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                />
                <Button onClick={handleSubmitNote} disabled={noteLoading || commentLoading || !noteInput.trim()} size="sm" className={`h-8 text-xs px-2 ${noteType === 'work_note' ? 'bg-rezolve-green hover:bg-rezolve-green/90' : 'bg-blue-500 hover:bg-blue-600'}`}>
                  {(noteLoading || commentLoading) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                </Button>
                {(noteSuccess || commentSuccess) && <CheckCircle className="h-4 w-4 text-green-500" />}
              </div>

              {/* Divider */}
              <div className={`w-px h-8 ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`} />

              {/* AI Response Generator - Compact */}
              <div className="flex items-center gap-1.5">
                <Wand2 className="h-4 w-4 text-rezolve-green flex-shrink-0" />
                <div className="flex gap-0.5">
                  <button onClick={() => setResponseTone('professional')} className={`text-[10px] px-1.5 py-1 rounded-l ${responseTone === 'professional' ? 'bg-rezolve-green text-white' : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>Pro</button>
                  <button onClick={() => setResponseTone('friendly')} className={`text-[10px] px-1.5 py-1 ${responseTone === 'friendly' ? 'bg-pink-500 text-white' : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>Fri</button>
                  <button onClick={() => setResponseTone('technical')} className={`text-[10px] px-1.5 py-1 rounded-r ${responseTone === 'technical' ? 'bg-blue-500 text-white' : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>Tech</button>
                </div>
                <Button onClick={handleGenerateResponse} disabled={responseLoading || !context} size="sm" className="bg-rezolve-green hover:bg-rezolve-green/90 h-8 text-xs">
                  {responseLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Wand2 className="h-3 w-3 mr-1" />Gen</>}
                </Button>
              </div>

              {/* Divider */}
              <div className={`w-px h-8 ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`} />

              {/* Record Call */}
              <div className="flex items-center gap-1.5">
                {isRecording ? (
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className={`text-xs ${isDark ? 'text-red-300' : 'text-red-600'}`}>Recording...</span>
                  </div>
                ) : recordingStatus === 'complete' ? (
                  <Button onClick={() => setShowNotesModal(true)} size="sm" className="bg-green-600 hover:bg-green-700 h-8 text-xs">
                    <Mic className="h-3 w-3 mr-1" />Notes
                  </Button>
                ) : (
                  <Button onClick={() => setShowRecorderModal(true)} size="sm" className="bg-red-600 hover:bg-red-700 h-8 text-xs">
                    <Mic className="h-3 w-3 mr-1" />Record
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recording modals */}
      <CallRecorderModal
        isOpen={showRecorderModal}
        onClose={() => setShowRecorderModal(false)}
        sysId={context?.sys_id}
        table={context?.table}
        recordDescription={context ? `${context.number}: ${context.short_description}` : undefined}
        onRecordingStart={() => {}}
      />
      <FloatingRecorderWidget
        onProcessingComplete={() => setShowNotesModal(true)}
      />
      <CallNotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        sysId={context?.sys_id}
        table={context?.table}
      />
    </div>
  )
}
